"use client";

import type { Table } from "@tanstack/react-table";
import { Check, Settings2 } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type {} from "@/i18n/locales/en/inventory";

type TranslationModule = Record<string, string>;

interface DataTableViewOptionsProps<TData>
  extends React.ComponentProps<typeof PopoverContent> {
  table: Table<TData>;
  translations?: TranslationModule;
}

export function DataTableViewOptions<TData>({
  table,
  translations,
  ...props
}: DataTableViewOptionsProps<TData>) {
  const columns = React.useMemo(
    () =>
      table
        .getAllColumns()
        .filter(
          (column) =>
            typeof column.accessorFn !== "undefined" && column.getCanHide(),
        ),
    [table],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label={translations?.toggleColumns || "Toggle columns"}
          role="combobox"
          variant="ghost"
          size="default"
          className="ml-auto hidden h-14 px-5 text-base font-normal rounded-full bg-white dark:bg-gray-900 border border-border hover:scale-105 active:scale-95 transition-all duration-300 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 lg:flex"
        >
          <Settings2 className="size-5 text-muted-foreground" />
          {translations?.view || "View"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-0" {...props}>
        <Command>
          <CommandInput placeholder={translations?.searchColumns || "Search columns..."} />
          <CommandList>
            <CommandEmpty>{translations?.noColumnsFound || "No columns found."}</CommandEmpty>
            <CommandGroup>
              {columns.map((column) => (
                <CommandItem
                  key={column.id}
                  onSelect={() =>
                    column.toggleVisibility(!column.getIsVisible())
                  }
                >
                  <span className="truncate">
                    {column.columnDef.meta?.label ?? column.id}
                  </span>
                  <Check
                    className={cn(
                      "ml-auto size-4 shrink-0",
                      column.getIsVisible() ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
